import { Form, Formik } from 'formik';
import * as bd from 'react-basic-design';
import { RedeemCodeDTO } from '../../../api/redeem-code-api';
import { LicenseDTO } from '../../../api/license-api';
import { ProductDTO } from '../../../api/product-api';
import { UserDTO } from '../../../api/user-api';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T } from '../../../components/basic/text';
import {
    FormikCellToggle,
    FormikDateInput,
    FormikNumberInput,
    FormikTextField,
} from '../../../components/formik';
import { yup } from '../../../components/basic/yup';
import SvgSave from '../../../assets/icons/Save';
import SvgDelete from '../../../assets/icons/Delete';
import SvgClose from '../../../assets/icons/Close';

interface SaveRedeemCodeModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedRedeemCode?: RedeemCodeDTO;
    licenses: LicenseDTO[];
    products: ProductDTO[];
    users: UserDTO[];
    onHide: () => void;
    onSave: (values: RedeemCodeDTO) => void;
    onDelete?: (redeemCode: RedeemCodeDTO) => void;
}

export function SaveRedeemCodeModal({
    show,
    allowEdit,
    selectedRedeemCode,
    licenses,
    products,
    users,
    onHide,
    onSave,
    onDelete,
}: SaveRedeemCodeModalProps) {
    const RedeemCodeSchema = yup.object().shape({
        code: yup.string().required(' '),
        productId: yup.number().required(' '),
        used: yup.boolean().required(' '),
        usedBy: yup.string(),
        usedAt: yup.date(),
        licenseSerial: yup.number(),
    });

    const defaultValues: RedeemCodeDTO = {
        id: selectedRedeemCode?.id,

        code: selectedRedeemCode ? selectedRedeemCode.code : '',
        productId: selectedRedeemCode ? selectedRedeemCode.productId : 0,
        used: selectedRedeemCode ? selectedRedeemCode.used : true,
        usedBy: selectedRedeemCode ? selectedRedeemCode.usedBy : '',
        usedAt: selectedRedeemCode ? selectedRedeemCode.usedAt : new Date(),

        licenseSerial: selectedRedeemCode ? selectedRedeemCode.licenseSerial : 0,
    };

    if (!show) return <></>;

    return (
        <Formik initialValues={defaultValues} validationSchema={RedeemCodeSchema} onSubmit={onSave}>
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    size="xl"
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <T>{selectedRedeemCode ? 'edit-redeem-code' : 'new-redeem-code'}</T>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!allowEdit}
                                >
                                    <SvgSave />
                                </bd.Button>
                                {selectedRedeemCode && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedRedeemCode)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap>
                            <FormikTextField
                                label="code"
                                name="code"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="product-id"
                                name="productId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...products.map(product => ({
                                        id: product.id,
                                        title: `${product.name} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikCellToggle
                                label={<T>used</T>}
                                name="used"
                                readOnly={!allowEdit}
                            />

                            <FormikTextField
                                label="used-by"
                                name="usedBy"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...users.map(user => ({
                                        id: user.userName,
                                        title: `${user.firstName} ${user.lastName} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikDateInput
                                label="used-at"
                                name="usedAt"
                                readOnly={!allowEdit}
                                inputMaxWidth="11rem"
                            />
                            <FormikTextField
                                label="license-serial"
                                name="licenseSerial"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...licenses.map(license => ({
                                        id: license.serial,
                                        title: `${license.licenseKey} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />
                        </bd.Flex>
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
